<template>
  <div class="app-container dc-page" v-loading="loading">
    <div class="dc-title">
      <i class="el-icon-connection" style="margin-right: 8px"></i>
      域名配置
    </div>

    <el-alert
      type="info"
      show-icon
      :closable="false"
      class="dc-alert"
      message="修改域名后无需重启服务，下次 API 调用时将自动使用新域名（缓存刷新间隔约 60 秒）。不同业务类型（GM / CX）可配置不同域名。"
    />

    <el-card v-for="svc in SERVICES" :key="svc.service" shadow="never" class="dc-card">
      <div slot="header" class="dc-card-head">
        <span>{{ svc.label }}</span>
        <span class="dc-hint">{{ svc.description }}</span>
      </div>
      <div v-for="biz in BIZ_TYPES" :key="biz.type" class="dc-row">
        <el-tag :type="biz.type === 'default' ? 'warning' : 'info'" class="dc-biz-tag" size="mini">
          {{ biz.label }}
        </el-tag>
        <el-input
          :value="editValues[configKey(svc.service, biz.type)] || ''"
          placeholder="https://..."
          class="dc-input"
          @input="(v) => setEditValue(configKey(svc.service, biz.type), v)"
        />
      </div>
      <div class="dc-actions">
        <el-button
          type="primary"
          icon="el-icon-check"
          :loading="saving === svc.service"
          :disabled="!hasChanges(svc.service)"
          @click="handleSaveAll(svc.service)"
        >
          保存
        </el-button>
        <span v-if="hasChanges(svc.service)" class="dc-changed">已修改，尚未保存</span>
      </div>
    </el-card>
  </div>
</template>

<script>
import { listSystemConfig, updateSystemConfig } from '@/api/customerService/systemConfig'

const SERVICES = [
  { service: 'fos', label: 'FOS 用户中心', description: '客户资料查询、存款提案查询、IMS 赠金活动等' },
  { service: 'activity', label: '活动管理平台', description: '新客优惠等活动信息查询（公开接口）' },
  { service: 'backoffice', label: 'Goldoffice Gateway', description: '存款提案后台详情（人民币金额、网关名称等）' }
]

const BIZ_TYPES = [
  { type: 'default', label: 'GM' },
  { type: 'cx', label: 'CX' }
]

export default {
  name: 'DomainConfigList',
  data () {
    return {
      SERVICES,
      BIZ_TYPES,
      configs: {},
      editValues: {},
      loading: true,
      saving: null
    }
  },
  created () {
    this.fetchConfigs()
  },
  methods: {
    configKey (service, bizType) {
      return `bridge.${service}.base-url.${bizType}`
    },
    async fetchConfigs () {
      this.loading = true
      try {
        const res = await listSystemConfig()
        const list = res.result || []
        const map = {}
        list.forEach(c => { map[c.configKey] = c.configValue })
        this.configs = map
        const edits = {}
        for (const svc of SERVICES) {
          for (const biz of BIZ_TYPES) {
            const k = this.configKey(svc.service, biz.type)
            edits[k] = map[k] || ''
          }
        }
        this.editValues = edits
      } catch (e) {
        this.$message.error('加载配置失败')
      } finally {
        this.loading = false
      }
    },
    setEditValue (key, value) {
      this.$set(this.editValues, key, value)
    },
    hasChanges (service) {
      return BIZ_TYPES.some(b => {
        const k = this.configKey(service, b.type)
        return ((this.editValues[k] || '').trim()) !== (this.configs[k] || '')
      })
    },
    async handleSaveAll (service) {
      const keys = BIZ_TYPES.map(b => this.configKey(service, b.type))
      const changed = keys.filter(k => (this.editValues[k] || '').trim() !== (this.configs[k] || ''))
      if (changed.length === 0) return

      this.saving = service
      try {
        for (const k of changed) {
          const v = (this.editValues[k] || '').trim()
          if (!v) {
            this.$message.warning('域名不能为空')
            this.saving = null
            return
          }
          await updateSystemConfig(k, v)
          this.$set(this.configs, k, v)
        }
        this.$message.success('保存成功，新域名将在下次 API 调用时生效')
      } catch (e) {
        this.$message.error('保存失败')
      } finally {
        this.saving = null
      }
    }
  }
}
</script>

<style scoped>
.dc-page {
  max-width: 900px;
  margin: 0 auto;
  min-height: 200px;
}

.dc-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.dc-alert {
  margin-bottom: 16px;
}

.dc-card {
  margin-bottom: 16px;
}

.dc-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
}

.dc-hint {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}

.dc-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.dc-biz-tag {
  width: 60px;
  flex-shrink: 0;
  justify-content: center;
  font-size: 13px;
}

.dc-input {
  flex: 1;
}

.dc-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.dc-changed {
  font-size: 12px;
  color: #e6a23c;
}
</style>